import React, { useState, useImperativeHandle, createContext, useContext, useEffect, useReducer } from 'react';
import { useToasts } from 'react-toast-notifications';
/** @jsxRuntime classic */

/** @jsx jsx */

import { jsx } from '@emotion/core';
import { CloseIcon, DeleteIcon, ExclamationTriangleIcon, FileAddIcon, FileIcon } from '../../../icon';
import style, { parentDiv } from './fileUploader.style';
import { mediaQuery } from '../../../utils';
import { getToken, getCSRFToken } from '../../../utils/api-client';
const FileUploaderContext = /*#__PURE__*/createContext();

function reducer(currentState, newState) {
  return { ...currentState,
    ...newState
  };
}

const sizeToMb = sizeInBytes => {
  return parseFloat((sizeInBytes / (1024 * 1024)).toFixed(2));
};
/**
 * This component is a uploader client.
 * @param {string} serverPath Server path (CDN path).
 * @param {File} file The file you wanna upload.
 * @param {function} onProgress On progress function.
 * @param {function} onDone On done function.
 */


async function uploaderClient(serverPath, file, onProgress, onDone) {
  const formData = new FormData();
  const xhr = new XMLHttpRequest();
  const token = getToken();
  const csrfToken = await getCSRFToken();
  formData.append('file', file);
  xhr.upload.onprogress = onProgress;

  xhr.onreadystatechange = () => {
    if (xhr.readyState === XMLHttpRequest.DONE) {
      onDone();
    }
  };

  xhr.open('POST', serverPath);
  xhr.withCredentials = true;

  if (token) {
    xhr.setRequestHeader('Authorization', `Bearer ${token}`);
  }

  xhr.setRequestHeader('X-CSRF-Token', csrfToken);
  xhr.send(formData);
  return xhr;
}
/**
 * This component is a uploader client.
 * @param {Array} accept Array of acepted extensions.
 * @param {text} text Uploader description.
 * @param {Object} inputRef Uploader ref.
 * @param {JSON} containerStyle Custom uploader style for the div container.
 * @param {string} viewType Type of view: grid\list.
 * @param {*} maxSize Max file size.
 * @param {JSON} containerStylePending Custom uploader style for the uploader when the file loading is pending.
 * @param {JSON} containerStyleDone Custom uploader style for the div uploader when the file loading is pending.
 * @param {JSON} containerStyleUploading Custom uploader style for the div container when the file is loading .
 * @param {JSON} containerStyleFragment Custom uploader style for the wrap div for the uploader .
 * @param {Object} className Used to define predetermine style to the uploader.
 */


const Uploader = ({
  accept,
  text,
  inputRef = null,
  containerStyle = {},
  viewType = 'grid',
  maxSize = '',
  containerStylePending = {},
  containerStyleDone = {},
  containerStyleUploading = {},
  containerStyleFragment = {},
  className,
  sumSizes,
  totalLimit
}) => {
  const {
    uploadFile,
    resetFileContext,
    state,
    setState,
    abortUpload,
    t,
    onFinishDelete,
    value,
    disabled = false
  } = useFileUploader();
  const {
    percentage,
    error
  } = state;
  let {
    fileName,
    uploadStatus
  } = state;
  const [timeoutError, setTimeoutError] = useState(null);
  const {
    addToast
  } = useToasts();

  if ((value === null || value === void 0 ? void 0 : value.length) > 0) {
    fileName = value;
    uploadStatus = 'done';
  }

  const deleteFile = () => {
    if (onFinishDelete) {
      onFinishDelete();
    }

    resetFileContext();
  };

  useImperativeHandle(inputRef, () => ({
    deleteFile() {
      resetFileContext();
    }

  }));
  useEffect(() => {
    if (error.length > 0) {
      if (timeoutError) {
        clearTimeout(timeoutError);
      }

      setTimeoutError(setTimeout(() => {
        setState({
          error: ''
        });
      }, 5000));
    } // eslint-disable-next-line react-hooks/exhaustive-deps

  }, [error]);

  const onFileChange = event => {
    if (uploadStatus === 'pending' || uploadStatus === 'done') {
      const file = event.target.files[0];
      const fileExt = file.name.split('.').pop();

      if (!accept.includes(fileExt)) {
        setState({
          error: t ? t('Seleccione un archivo en un formato permitido') : 'Seleccione un archivo en un formato permitido'
        });
        return;
      }

      if (maxSize) {
        const fileSize = sizeToMb(file.size);

        if (fileSize > parseFloat(maxSize)) {
          setState({
            error: t ? t('El archivo no puede ser mayor a', {
              maxSize
            }) : `El archivo no puede pesar más de ${maxSize}MB.`
          });
          return;
        }
      }

      if (sumSizes) {
        const fileSize = sizeToMb(file.size);

        if (sumSizes + fileSize > totalLimit) {
          setState({
            error: t ? t('Límite total excedido') : 'Límite total excedido'
          });
          addToast(`Este archivo supera el límite del tamaño total de los archivos. Por favor ingrese menos de 
             ${totalLimit} MG en total.`, {
            appearance: 'error',
            autoDismiss: true
          });
          return;
        }
      }

      const reader = new FileReader();
      reader.readAsDataURL(file);
      uploadFile(event.target.files[0]);
    }
  };

  const pendingClass = (className === null || className === void 0 ? void 0 : className.pending) || 'pending';
  const uploadStatusClass = (className === null || className === void 0 ? void 0 : className.uploadStatus) || `${pendingClass} ${viewType}`;
  const fileAddIconClass = (className === null || className === void 0 ? void 0 : className.fileAddIcon) || 'file-add-icon';
  const spanClass = (className === null || className === void 0 ? void 0 : className.span) || 'unselected-file';
  const inputClass = (className === null || className === void 0 ? void 0 : className.input) || null;
  const small = (className === null || className === void 0 ? void 0 : className.uploadingSize) === 'small';
  const padding = small ? '0px' : '22px 28px';
  const alignItems = small ? 'normal' : 'center';
  return jsx("div", {
    css: parentDiv({
      containerStyle
    })
  }, jsx("div", {
    css: style
  }, uploadStatus === 'pending' ? jsx("div", {
    className: uploadStatusClass,
    style: containerStylePending
  }, error ? jsx("div", {
    className: "row-file-uploader",
    style: {
      minHeight: 80
    }
  }, jsx("div", {
    className: "column-file-uploader",
    style: {
      flexDirection: 'row'
    }
  }, jsx(ExclamationTriangleIcon, {
    className: "file-error-icon"
  })), jsx("div", {
    className: "column-file-uploader",
    style: {
      flexDirection: 'row',
      flexBasis: '45%'
    }
  }, jsx("span", {
    className: "error"
  }, error))) : jsx(React.Fragment, null, jsx("div", {
    className: "row-file-uploader",
    css: { ...containerStyleFragment,
      [mediaQuery[0]]: {
        padding: 20
      }
    }
  }, jsx("div", {
    className: "column-file-uploader",
    style: {
      flexDirection: 'row'
    }
  }, jsx(FileAddIcon, {
    className: `${fileAddIconClass} ${viewType === 'grid' ? '' : 'file-add-icon-grid-view'}`
  }), jsx("span", {
    className: spanClass
  }, text))), jsx("input", {
    className: inputClass,
    type: "file",
    accept: accept,
    onChange: onFileChange
  }))) : '', uploadStatus === 'uploading' ? jsx("div", {
    className: small ? 'uploading-small' : 'uploading',
    style: containerStyleUploading
  }, jsx("div", {
    className: "row-file-uploader",
    style: {
      padding,
      ...containerStyleFragment
    }
  }, jsx("div", {
    className: "column-file-uploader",
    style: viewType === 'grid' ? {
      alignItems
    } : {
      flexBasis: '65%'
    }
  }, small ? jsx(React.Fragment, null, jsx("div", {
    className: "progress-bar-div-small"
  }, jsx("div", {
    className: "progress-bar-small"
  }, jsx("div", {
    style: {
      width: `${percentage}%`
    }
  })), viewType === 'grid' ? jsx("span", {
    className: "percentage-number-small"
  }, percentage, "%") : null, jsx(CloseIcon, {
    className: "abort-icon",
    onClick: () => abortUpload()
  })), jsx("span", {
    className: "percentage-text-small"
  }, t ? t('Subiendo archivo') : 'Subiendo archivo')) : jsx(React.Fragment, null, viewType === 'grid' ? jsx("span", {
    className: "percentage-number"
  }, percentage, "%") : null, jsx("div", {
    className: "progress-bar-div"
  }, jsx("div", {
    className: "progress-bar"
  }, jsx("div", {
    style: {
      width: `${percentage}%`
    }
  })), jsx(CloseIcon, {
    className: "abort-icon",
    onClick: () => abortUpload()
  })), jsx("span", {
    className: "percentage-text"
  }, t ? t('Subiendo archivo') : 'Subiendo archivo')))), viewType !== 'grid' ? jsx("div", {
    className: "column-file-uploader",
    style: {
      alignItems: 'flex-end'
    }
  }, jsx("span", {
    className: "percentage-number"
  }, percentage, "%")) : null) : '', uploadStatus === 'done' ? jsx("div", {
    className: "done",
    style: containerStyleDone
  }, viewType === 'grid' ? jsx(React.Fragment, null, jsx("div", {
    className: "row-file-uploader",
    style: {
      padding,
      ...containerStyleFragment
    }
  }, jsx("div", {
    className: "column-file-uploader",
    style: {
      flexDirection: 'row'
    }
  }, jsx(FileIcon, {
    className: "file-icon"
  }), jsx("span", {
    className: "file-selected"
  }, fileName))), !disabled && jsx("div", {
    className: "remove-file-div"
  }, jsx("button", {
    type: "button",
    onClick: !disabled ? deleteFile : {},
    disabled: disabled
  }, jsx(DeleteIcon, {
    className: "delete-icon"
  }), jsx("span", null, t ? t('Eliminar') : 'Eliminar')))) : jsx("div", {
    className: "row-file-uploader",
    style: {
      padding,
      ...containerStyleFragment
    }
  }, jsx("div", {
    className: "column-file-uploader",
    style: {
      flexDirection: 'row',
      flexBasis: '65%'
    }
  }, jsx(FileIcon, {
    className: "file-icon"
  }), jsx("span", {
    className: "file-selected"
  }, fileName)), jsx("div", {
    className: "column-file-uploader",
    style: {
      flexDirection: 'row'
    }
  }, jsx("button", {
    type: "button",
    onClick: !disabled ? deleteFile : {},
    disabled: disabled,
    style: {
      justifyContent: 'flex-end'
    }
  }, jsx(DeleteIcon, {
    className: "delete-icon"
  }))))) : ''));
};
/**
 * This component is a the file Uploader provider, here you can use the uploader.
 * @param {*} children FileUploader client.
 * @param {function} onFinish Function triggered when the loading of the file is finish.
 * @param {string} serverPath Server path (CDN path).
 * @param {boolean} clearOnFinish Determines if the provider is cleared after the load of the file.
 * @param {function} t Translate.
 * @param {function} onFinishDelete Function for delete cache when finish the delete.
 */


function FileUploaderProvider({
  children,
  onFinish,
  serverPath,
  clearOnFinish = false,
  t = null,
  onFinishDelete,
  ...rest
}) {
  const [state, setState] = useReducer(reducer, {
    fileName: '',
    percentage: 0,
    XMLHttpRequest: null,
    uploadStatus: 'pending',
    error: ''
  });

  const resetFileContext = () => {
    setState({
      fileName: '',
      percentage: 0,
      XMLHttpRequest: null,
      uploadStatus: 'pending'
    });
  };

  const uploadFile = async file => {
    let fileName = file.name;

    if (fileName.length > 17) {
      fileName = `${fileName.substr(0, 7)}...${fileName.substr(fileName.length - 7, fileName.length)}`;
    }

    setState({
      fileName
    });
    const XMLHttpRequest = await uploaderClient(serverPath, file, e => {
      const done = e.position || e.loaded;
      const total = e.totalSize || e.total;
      const perc = Math.floor(done / total * 1000) / 10;

      if (perc < 100) {
        setState({
          uploadStatus: 'uploading'
        });
      }

      setState({
        percentage: perc
      });
    }, () => {
      const response = XMLHttpRequest.status === 200 && XMLHttpRequest.response ? { ...JSON.parse(XMLHttpRequest.response),
        fileName: file.name,
        fileSize: file.size
      } : {};
      let error = '';

      if (XMLHttpRequest.status !== 200) {
        error = t ? t('Problemas al subir el archivo, intente de nuevo') : 'Problemas al subir el archivo, intente de nuevo';
      }

      if (XMLHttpRequest.isAborted) {
        error = '';
      }

      setState({
        error,
        uploadStatus: XMLHttpRequest.status === 200 ? 'done' : 'pending',
        XMLHttpRequest: null
      });
      onFinish(response);

      if (clearOnFinish) {
        resetFileContext();
      }
    });
    setState({
      XMLHttpRequest
    });
  };

  const abortUpload = () => {
    const {
      XMLHttpRequest
    } = state;

    if (XMLHttpRequest) {
      XMLHttpRequest.isAborted = true;
      XMLHttpRequest.abort();
    }

    setState({
      fileName: '',
      uploadStatus: 'pending',
      percentage: 0
    });
    onFinish({});
  };

  return jsx(FileUploaderContext.Provider, {
    value: {
      uploadFile,
      abortUpload,
      resetFileContext,
      state,
      setState,
      t,
      onFinishDelete,
      ...rest
    }
  }, children);
}

function useFileUploader() {
  const context = useContext(FileUploaderContext);

  if (context === undefined) {
    throw new Error(`useFileUploader must be used within a FileUploaderProvider`);
  }

  return context;
}

export { FileUploaderProvider, useFileUploader, Uploader };